import { Email, URI, UUID } from "./common";
import { JobTypeId } from "./jobTypes";
import { ListedUser } from "./users";

export enum JobStatus {
  NOT_STARTED = "not_started",
  WAITING = "waiting",
  RUNNING = "running",
  CANCELLED = "cancelled",
  COMPLETED = "completed",
  FAILED = "failed",
  BLOCKED = "blocked",
}

export interface JobProgress {
  current: number;
  total: number;
}

interface ClaimedByUser extends ListedUser {
  claimed_until: number;
}

export interface ListedJob {
  blocking_step: string | null;
  claimed_by: ClaimedByUser | null;
  date_created: number;
  date_modified: number;
  error_message: string | null;
  id: UUID;
  name: string;
  owner: ListedUser;
  priority: number;
  progress: JobProgress | null;
  status: JobStatus;
  type: JobTypeId;
}

export interface TeamCollaboration {
  id: UUID;
  name: string;
  steps: Array<string>;
}
export interface UserCollaboration {
  email: Email;
  id: UUID;
  name: string;
  steps: Array<string>;
}
/**
 * @example {
 *   "teams": [
 *     {
 *        "id":"584df90d-0810-492b-ac3b-98609f4ab259",
 *        "steps": ["*"]
 *     }
 *   ],
 *   "users": [
 *     {
 *        "id":"226dc325-cbf7-4cd1-ab31-2a8ef27587c9",
 *        "steps": ["*"]
 *     }
 *   ]
 * }
 */
export interface ApiJobCollaboration {
  teams: Array<TeamCollaboration>;
  users: Array<UserCollaboration>;
}

/**
 * @description The content role (input, output, or something else).
 * @isString Field must be a string.
 * @example "input"
 */
export type JobContentRole = string;

export interface ApiJobContent {
  content_hash: string | null;
  modified: number | null;
  name: string;
  order: number;
  readonly: boolean;
  requires_auth: boolean;
  role: JobContentRole;
  size: number;
  type: string;
  uri: URI;
  uri_valid_until: number | null;
}

export interface InputJobContent extends ApiJobContent {
  role: "input";
}
export interface OutputJobContent extends ApiJobContent {
  role: "output";
}

export interface ApiJob extends ListedJob {
  collaboration: ApiJobCollaboration;
  error_message: string | null;
  input_content: InputJobContent;
  metadata: {
    [key: string]: any;
  };
  output_content: OutputJobContent | null;
  owner: ListedUser;
  use_for_training: boolean;
  parent_id:string;
  warnings: JobWarning[];
}

export enum WarningType {
  BAD_SUPPORT = "bad_support",
  STRANGE_VALUE = "strange_value",
  UNREVIEWED_DOC = "unreviewed_doc",
  INEXACT_VALUE = "inexact_value",
  EMPTY_DOC = "empty_doc"
}

export interface JobWarning {
  type: WarningType;
  dataPoint?: string;
  value?: string;
  date?: Date;
  message?: String;
  resolutionDate?: Date;
}

export interface TeamCollaborationByIdRequest {
  id: UUID;
  steps?: Array<string>;
}
export interface TeamCollaborationByNameRequest {
  name: string;
  steps?: Array<string>;
}
export interface UserCollaborationByIdRequest {
  id: UUID;
  steps?: Array<string>;
}
export interface UserCollaborationByEmailRequest {
  email: Email;
  steps?: Array<string>;
}
export type TeamCollaborationRequest =
  | TeamCollaborationByIdRequest
  | TeamCollaborationByNameRequest;
export type UserCollaborationRequest =
  | UserCollaborationByIdRequest
  | UserCollaborationByEmailRequest;
/**
 * @example {
 *   "teams": [
 *     {
 *        "id":"584df90d-0810-492b-ac3b-98609f4ab259",
 *        "steps": ["*"]
 *     }
 *   ],
 *   "users": [
 *     {
 *        "id":"226dc325-cbf7-4cd1-ab31-2a8ef27587c9",
 *        "steps": ["*"]
 *     }
 *   ]
 * }
 */
interface JobCollaborationRequest {
  teams?: Array<TeamCollaborationRequest>;
  users?: Array<UserCollaborationRequest>;
}
interface InputContentRequest {
  /**
   * The name of the input content.
   * @isString Field must be a string.
   * @example "My file.pdf"
   */
  name?: string;
  /**
   * A flag indicating whether this content should be readonly.
   * @isBool Field must be a boolean.
   * @example true
   */
  readonly?: boolean;
  /**
   * Role must be "input".
   */
  role: "input";
  /**
   * The size of the input content in bytes.
   * @isInt Field must be an integer.
   * @example 123456
   */
  size?: number;
  /**
   * The mimetype of the input content.
   * @isString Field must be a string.
   * @example "application/pdf"
   */
  type?: string;
  /**
   * A URI pointing to the content. Usually this field is populated with the content of an upload to
   * POST /documents
   * @isString Field must be a URI.
   * @example "https://api.innodata.com/v1.1/documents/fdafa34f-ff00-4b20-8d11-259d069bb0c7/contents"
   */
  uri: URI;
}

export interface CreateJobRequest {
  /**
   * An optional set of job collaborators.
   */
  collaboration?: JobCollaborationRequest;
  /**
   * The content to use as "input" for the job.
   */
  input_content: InputContentRequest;
  /**
   * Any additional metadata to attach to the job.
   */
  metadata?: {
    [key: string]: any;
  };
  /**
   * The priority of the job.
   *
   * @isInt Field must be an integer.
   * @min 0
   * @max 99
   * @example 50
   */
  priority?: number;
  /**
   * The type of the job.
   */
  type: JobTypeId;
}
export interface UpdateJobRequest {
  blocked_on_step?: string;
  collaboration?: JobCollaborationRequest | null;
  error_message?: string | null;
  metadata?: {
    [key: string]: any;
  };
  progress?: JobProgress | null;
  status?: JobStatus;
}

export enum JobCheckinStatus {
  ABORTED = "aborted",
  COMPLETED = "completed",
  FAILED = "failed",
  CLAIMED = "claimed",
}
export interface CheckinJobRequest {
  message?: string;
  status: JobCheckinStatus;
}

export type AddJobCollaborationRequest = JobCollaborationRequest;
export type RemoveJobCollaborationRequest = JobCollaborationRequest;
export type ReplaceJobCollaborationRequest = JobCollaborationRequest;

export enum JobEvent {
  JobBlocked = "JOB BLOCKED",
  JobCancelled = "JOB CANCELLED",
  JobCheckedIn = "JOB CHECKED IN",
  JobCheckedOut = "JOB CHECKED OUT",
  JobCollaborationChanged = "JOB COLLABORATION CHANGED",
  JobCompleted = "JOB COMPLETED",
  JobContentChanged = "JOB CONTENT CHANGED",
  JobCreated = "JOB CREATED",
  JobDeleted = "JOB DELETED",
  JobFailed = "JOB FAILED",
  JobProgressChanged = "JOB PROGRESS CHANGED",
  JobNotesChanged = "JOB NOTES CHANGED",
  JobStatusChanged = "JOB STATUS CHANGED",
}

export const isJobEvent = (type: any): type is JobEvent => {
  return typeof type === "string" && Object.values(JobEvent).includes(type as any);
};

export interface ApiJobNote {
  id: UUID;
  content: string;
  created: number;
  edited: number | null;
  owner: ListedUser;
}

export interface NewJobNote {
  content: string;
}
export type UpdateJobNote = NewJobNote;
